import type { CharacterState } from '../character/CharacterStateMachine';
import { BUILD_TAG } from '../buildTag';

export class HUD {
  private root: HTMLDivElement;
  private stateEl: HTMLDivElement;
  private fpsEl: HTMLDivElement;
  private crosshair: HTMLDivElement;
  private grabPrompt: HTMLDivElement;
  private roomEl: HTMLDivElement;
  private netEl: HTMLDivElement;
  private staminaFill: HTMLDivElement;
  private staminaWrap: HTMLDivElement;
  private frames = 0;
  private accum = 0;
  private fps = 0;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:absolute;inset:0;font-family:ui-monospace,monospace;color:#e8e8ef;font-size:12px;pointer-events:none;';

    this.crosshair = document.createElement('div');
    this.crosshair.style.cssText =
      'position:absolute;left:50%;top:50%;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;background:rgba(255,255,255,0.6);box-shadow:0 0 4px rgba(0,0,0,0.8);';
    this.root.appendChild(this.crosshair);

    this.stateEl = document.createElement('div');
    this.stateEl.style.cssText = 'position:absolute;left:12px;bottom:12px;opacity:0.85;display:none;';
    this.root.appendChild(this.stateEl);

    this.grabPrompt = document.createElement('div');
    this.grabPrompt.style.cssText =
      'position:absolute;left:50%;bottom:64px;transform:translateX(-50%);' +
      'padding:6px 14px;border:1px solid rgba(93,255,212,0.7);border-radius:8px;' +
      'background:rgba(0,0,0,0.5);color:#5dffd4;display:none;white-space:nowrap;';
    this.grabPrompt.textContent = 'TAHAN KLIK KIRI UNTUK TARIK / DORONG';
    this.root.appendChild(this.grabPrompt);

    this.fpsEl = document.createElement('div');
    this.fpsEl.style.cssText = 'position:absolute;right:12px;bottom:12px;opacity:0.85;display:none;';
    this.root.appendChild(this.fpsEl);

    const tagEl = document.createElement('div');
    tagEl.style.cssText = 'position:absolute;left:12px;top:10px;opacity:0.45;';
    tagEl.textContent = `v${BUILD_TAG}`;
    this.root.appendChild(tagEl);

    this.roomEl = document.createElement('div');
    this.roomEl.style.cssText =
      'position:absolute;left:50%;top:10px;transform:translateX(-50%);' +
      'padding:4px 12px;border:1px solid rgba(93,255,212,0.5);border-radius:8px;' +
      'background:rgba(0,0,0,0.5);color:#5dffd4;display:none;white-space:nowrap;';
    this.root.appendChild(this.roomEl);

    this.netEl = document.createElement('div');
    this.netEl.style.cssText =
      'position:absolute;left:50%;top:40px;transform:translateX(-50%);' +
      'font-size:11px;opacity:0.7;display:none;white-space:nowrap;';
    this.root.appendChild(this.netEl);

    // Debug (STATE/FPS/NET) tersembunyi default; dibuka lewat Settings.
    this.debugEls = [this.stateEl, this.fpsEl, this.netEl];

    this.platformEl = document.createElement('div');
    this.platformEl.style.cssText =
      'position:absolute;left:50%;top:58px;transform:translateX(-50%);' +
      'font-size:11px;opacity:0.8;display:none;white-space:nowrap;' +
      'padding:3px 10px;border:1px solid rgba(255,255,255,0.18);border-radius:8px;' +
      'background:rgba(0,0,0,0.4);';
    this.root.appendChild(this.platformEl);

    // Bar stamina (tarik-lari).
    this.staminaWrap = document.createElement('div');
    this.staminaWrap.style.cssText =
      'position:absolute;left:50%;bottom:96px;transform:translateX(-50%);' +
      'width:160px;height:8px;border:1px solid rgba(255,255,255,0.25);' +
      'border-radius:5px;background:rgba(0,0,0,0.5);overflow:hidden;';
    this.staminaFill = document.createElement('div');
    this.staminaFill.style.cssText =
      'width:100%;height:100%;background:#5dffd4;border-radius:4px;';
    this.staminaWrap.appendChild(this.staminaFill);
    this.root.appendChild(this.staminaWrap);

    container.appendChild(this.root);
  }

  update(delta: number, state: CharacterState): void {
    this.stateEl.textContent = `STATE: ${state}`;
    this.frames++;
    this.accum += delta;
    if (this.accum >= 0.5) {
      this.fps = Math.round(this.frames / this.accum);
      this.frames = 0;
      this.accum = 0;
      this.fpsEl.textContent = `${this.fps} FPS`;
    }
    if (this.hintEl && this.hintTimer > 0) {
      this.hintTimer -= delta;
      if (this.hintTimer <= 0) this.hintEl.style.display = 'none';
    }
  }

  /** Hint sementara (progress gate/bagian stage) di tengah atas. */
  showHint(text: string | null, ms = 2600): void {
    if (!this.hintEl) {
      this.hintEl = document.createElement('div');
      this.hintEl.style.cssText =
        'position:absolute;left:50%;top:76px;transform:translateX(-50%);' +
        'padding:6px 14px;font-size:13px;font-weight:600;color:#ffe08a;' +
        'background:rgba(10,10,15,0.72);border:1px solid rgba(255,224,138,0.45);' +
        'border-radius:10px;white-space:nowrap;z-index:4;pointer-events:none;';
      this.root.appendChild(this.hintEl);
    }
    if (text === null) {
      this.hintEl.style.display = 'none';
      this.hintTimer = 0;
      return;
    }
    this.hintEl.textContent = text;
    this.hintEl.style.display = 'block';
    this.hintTimer = ms;
  }

  /** Tujuan bagian aktif (persisten sampai diganti) — pemandu arah. */
  setObjective(text: string | null): void {
    const next = text ?? '';
    if (next === this.objectiveText) return;
    this.objectiveText = next;
    if (!this.objectiveEl) {
      this.objectiveEl = document.createElement('div');
      this.objectiveEl.style.cssText =
        'position:absolute;left:12px;top:48px;opacity:0.9;font-size:12px;' +
        'color:#ffe08a;text-shadow:0 1px 3px rgba(0,0,0,0.7);pointer-events:none;';
      this.root.appendChild(this.objectiveEl);
    }
    this.objectiveEl.textContent = next;
    this.objectiveEl.style.display = next ? 'block' : 'none';
  }

  setCrosshairVisible(visible: boolean): void {
    this.crosshair.style.display = visible ? 'block' : 'none';
  }

  setGrabPrompt(visible: boolean): void {
    this.grabPrompt.style.display = visible ? 'block' : 'none';
  }

  setRoomInfo(text: string): void {
    this.roomEl.textContent = text;
    this.roomEl.style.display = text ? 'block' : 'none';
  }

  /** Stamina 0..100 dengan warna berjenjang. */
  setStamina(pct: number): void {
    const p = Math.max(0, Math.min(100, pct));
    this.staminaFill.style.width = `${p}%`;
    this.staminaFill.style.background =
      p > 50 ? '#5dffd4' : p > 25 ? '#ffd166' : '#ff6b6b';
  }

  private stageEl!: HTMLDivElement;

  setStageLabel(text: string): void {
    if (!this.stageEl) {
      this.stageEl = document.createElement('div');
      this.stageEl.style.cssText =
        'position:absolute;left:12px;top:30px;opacity:0.7;font-size:12px;';
      this.root.appendChild(this.stageEl);
    }
    this.stageEl.textContent = text;
  }

  setNetInfo(text: string): void {
    if (this.netEl.textContent === text) return;
    this.netEl.textContent = text;
    this.netEl.style.display = text && this.debugVisible ? 'block' : 'none';
  }

  private platformEl!: HTMLDivElement;
  private hintEl: HTMLDivElement | null = null;
  private hintTimer = 0;
  private objectiveEl: HTMLDivElement | null = null;
  private objectiveText = '';

  /** Chip status platform bergerak/jembatan (S7-S9). Kosong = sembunyi. */
  setPlatformChip(text: string): void {
    if (this.platformEl.textContent === text) return;
    this.platformEl.textContent = text;
    this.platformEl.style.display = text ? 'block' : 'none';
  }

  /** Mode foto: sembunyikan seluruh HUD agar jepretan bersih. */
  setPhotoMode(on: boolean): void {
    this.root.style.display = on ? 'none' : 'block';
  }

  private debugEls: HTMLDivElement[] = [];
  private debugVisible = false;

  /** Info debug (STATE/FPS/NET): toggle dari Settings. */
  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    for (const el of this.debugEls) {
      el.style.display = visible ? 'block' : 'none';
    }
  }

  dispose(): void {
    this.root.remove();
  }
}